'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { heroSlideService, DBHeroSlide } from '@/lib/services/dataService';

export interface HeroSlide {
  id: number;
  image: string;
  mobile_image?: string;
  alt: string;
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
}

const defaultSlides: HeroSlide[] = [
  {
    id: 1,
    image: '/assets/images/ChatGPT_Image_Jun_3__2026__10_54_17_AM-1780489023621.png',
    alt: 'Modern residential apartment building with lush greenery',
    title: 'Find Your Dream Home',
    description: 'Browse thousands of rental properties across India. Verified listings, transparent pricing.',
    ctaText: 'Explore Rentals',
    ctaLink: '#listings',
  },
  {
    id: 2,
    image: '/assets/images/ChatGPT_Image_Jun_3__2026__04_27_42_PM-1780489026733.png',
    alt: 'Contemporary commercial office space with city view',
    title: 'Rent Commercial Spaces',
    description: 'Find offices, shops and business spaces easily. Prime locations, flexible terms.',
    ctaText: 'Browse Spaces',
    ctaLink: '#listings',
  },
  {
    id: 3,
    image: '/assets/images/ChatGPT_Image_Jun_3__2026__03_38_05_PM-1780489031299.png',
    alt: 'Elegant furnished living room interior with modern decor',
    title: 'List Your Property',
    description: 'Upload your property and start earning today. Reach thousands of verified tenants.',
    ctaText: 'List Now',
    ctaLink: '/owner-dashboard',
  },
  {
    id: 4,
    image: '/assets/images/ChatGPT_Image_Jun_3__2026__03_38_10_PM-1780489037305.png',
    alt: 'Spacious modern kitchen with island counter and appliances',
    title: 'Premium Furnished Homes',
    description: 'Move-in ready homes with top-tier amenities. No hassle, just comfort.',
    ctaText: 'View Homes',
    ctaLink: '#listings',
  },
  {
    id: 5,
    image: '/assets/images/ChatGPT_Image_Jun_3__2026__03_38_13_PM-1780489048677.png',
    alt: 'Luxury apartment rooftop terrace with city skyline at sunset',
    title: 'Verified & Trusted Listings',
    description: 'Every property is verified by our team. Safe, secure and reliable rentals.',
    ctaText: 'Start Searching',
    ctaLink: '#listings',
  },
];

function dbSlideToHeroSlide(s: DBHeroSlide, idx: number): HeroSlide {
  return {
    id: idx + 1,
    image: s.image,
    mobile_image: s.mobile_image || '',
    alt: s.alt,
    title: s.title,
    description: s.description,
    ctaText: s.cta_text,
    ctaLink: s.cta_link,
  };
}

function isValidImageUrl(url: string): boolean {
  if (!url || !url.trim()) return false;
  // Reject local file paths
  if (/^[a-zA-Z]:\\/.test(url)) return false;
  if (/^\/Users\//i.test(url)) return false;
  if (/^\/home\//i.test(url)) return false;
  if (url.startsWith('file://')) return false;
  return true;
}

interface HeroCarouselProps {
  slides?: HeroSlide[];
}

export default function HeroCarousel({ slides: propSlides }: HeroCarouselProps) {
  const [slides, setSlides] = useState<HeroSlide[]>(propSlides || defaultSlides);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const total = slides.length;

  useEffect(() => {
    setIsMounted(true);
    if (!propSlides) {
      heroSlideService.getActiveSlides().then((data) => {
        if (data && data.length > 0) {
          setSlides(data.map(dbSlideToHeroSlide));
        }
      });
    }
  }, [propSlides]);

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setActiveIndex(((index % total) + total) % total);
      setTimeout(() => setAnimating(false), 700);
    },
    [animating, total]
  );

  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, 3000);
  }, [total]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isHovered) startInterval();
    else stopInterval();
    return stopInterval;
  }, [isHovered, startInterval, stopInterval]);

  const currentSlide = slides[activeIndex];

  return (
    <section
      suppressHydrationWarning
      className="relative w-full overflow-hidden bg-gray-900"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Responsive container: aspect-ratio on mobile, fixed height on desktop */}
      <div
        className="relative w-full"
        style={{
          paddingBottom: 'clamp(0px, 0px, 0px)',
        }}
      >
        {/* Mobile: aspect ratio wrapper so image shows fully */}
        <div className="block sm:hidden" style={{ aspectRatio: '3/4', position: 'relative', width: '100%' }}>
          {slides.map((slide, i) => {
            const mobileImg = slide.mobile_image && slide.mobile_image.trim() !== '' ? slide.mobile_image : slide.image;
            const mobileSrc = isValidImageUrl(mobileImg) ? mobileImg : '/assets/images/no_image.png';
            return (
            <div
              key={slide.id}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{ opacity: i === activeIndex ? 1 : 0, zIndex: i === activeIndex ? 1 : 0 }}
              aria-hidden={i !== activeIndex}
            >
              <Image
                src={mobileSrc}
                alt={slide.alt}
                fill
                priority={i === 0}
                sizes="100vw"
                className="object-cover object-center"
                style={{
                  transform: i === activeIndex ? 'scale(1)' : 'scale(1.02)',
                  transition: 'transform 6s ease-out',
                }}
              />
            </div>
            );
          })}

          {/* Mobile Left Arrow */}
          <button
            suppressHydrationWarning
            onClick={prev}
            className="hero-arrow absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full flex items-center justify-center"
            aria-label="Previous slide"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Mobile Right Arrow */}
          <button
            suppressHydrationWarning
            onClick={next}
            className="hero-arrow absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full flex items-center justify-center"
            aria-label="Next slide"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Mobile Pagination Dots */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                suppressHydrationWarning
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="rounded-full transition-all duration-300 focus:outline-none"
                style={{
                  width: i === activeIndex ? '20px' : '6px',
                  height: '6px',
                  background: i === activeIndex
                    ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                    : 'rgba(255,255,255,0.45)',
                  boxShadow: i === activeIndex ? '0 0 8px rgba(245,158,11,0.6)' : 'none',
                }}
              />
            ))}
          </div>

          {/* Mobile Text Content — overlaid at bottom of image */}
          <div
            suppressHydrationWarning
            className="absolute bottom-0 left-0 right-0 z-10"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.42) 65%, transparent 100%)',
              paddingBottom: '2.5rem',
              paddingTop: '2rem',
            }}
          >
            <div
              key={activeIndex}
              className="hero-text-enter flex flex-col gap-3 px-4"
            >
              <div>
                <h1
                  className="text-xl font-bold text-white leading-tight mb-1"
                  style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
                >
                  {currentSlide.title}
                </h1>
                <p className="text-sm text-white/80 leading-relaxed">
                  {currentSlide.description}
                </p>
              </div>
              <a
                href={currentSlide.ctaLink}
                className="hero-cta inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white self-start"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                  boxShadow: '0 4px 16px rgba(245,158,11,0.4)',
                }}
              >
                {currentSlide.ctaText}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Desktop: fixed height */}
        <div className="hidden sm:block" style={{ height: 'clamp(420px, 70vh, 780px)', position: 'relative' }}>
          {slides.map((slide, i) => {
            const desktopSrc = isValidImageUrl(slide.image) ? slide.image : '/assets/images/no_image.png';
            return (
            <div
              key={slide.id}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{ opacity: i === activeIndex ? 1 : 0, zIndex: i === activeIndex ? 1 : 0 }}
              aria-hidden={i !== activeIndex}
            >
              <Image
                src={desktopSrc}
                alt={slide.alt}
                fill
                priority={i === 0}
                sizes="100vw"
                className="object-cover object-center"
                style={{
                  transform: i === activeIndex ? 'scale(1)' : 'scale(1.04)',
                  transition: 'transform 6s ease-out',
                }}
              />
            </div>
            );
          })}

          {/* Desktop Left Arrow */}
          <button
            suppressHydrationWarning
            onClick={prev}
            className="hero-arrow absolute left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center"
            aria-label="Previous slide"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Desktop Right Arrow */}
          <button
            suppressHydrationWarning
            onClick={next}
            className="hero-arrow absolute right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center"
            aria-label="Next slide"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Desktop Pagination Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
            {slides.map((_, i) => (
              <button
                suppressHydrationWarning
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="rounded-full transition-all duration-300 focus:outline-none"
                style={{
                  width: i === activeIndex ? '28px' : '8px',
                  height: '8px',
                  background: i === activeIndex
                    ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                    : 'rgba(255,255,255,0.45)',
                  boxShadow: i === activeIndex ? '0 0 10px rgba(245,158,11,0.6)' : 'none',
                }}
              />
            ))}
          </div>

          {/* Desktop Slide counter */}
          <div className="absolute bottom-6 right-5 z-20 text-white/55 text-xs font-mono" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            {String(activeIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </div>

          {/* Desktop Pause indicator */}
          <div
            className="absolute top-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-white/70 transition-opacity duration-200"
            style={{
              background: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(8px)',
              opacity: isMounted && isHovered ? 1 : 0,
              pointerEvents: 'none',
            }}
          >
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Paused
          </div>

          {/* Desktop Text Content — bottom bar with gradient backdrop */}
          <div
            suppressHydrationWarning
            className="absolute bottom-0 left-0 right-0 z-10"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.38) 70%, transparent 100%)',
              paddingBottom: '3rem',
              paddingTop: '2.5rem',
            }}
          >
            <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
              <div
                key={activeIndex}
                className="hero-text-enter flex flex-row items-end justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <h1
                    className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-2"
                    style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
                  >
                    {currentSlide.title}
                  </h1>
                  <p
                    className="text-base text-white/85 leading-relaxed"
                    style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}
                  >
                    {currentSlide.description}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href="#listings"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('listings')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="hero-cta inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-base text-white whitespace-nowrap"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                      boxShadow: '0 4px 20px rgba(245,158,11,0.4)',
                    }}
                  >
                    {currentSlide.ctaText}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
